import {usePageSeo} from '@/lib/seo';
import {hasPlantAreas,hasCropCultivation} from '@/lib/subjects';
import MushroomCultivation,{hasMushroomContent} from '@/components/MushroomCultivation';
import { MycorrhizaSummary } from '@/components/MycorrhizaField';
import {SocialLinks,SubjectVideo} from '@/components/SubjectMediaFields';
import {metadataText} from '@/components/MetadataLanguageField';
import CropIdentity from "@/components/CropIdentity";
import ReferenceCatalog from "@/components/ReferenceCatalog";
import GalleryView from "@/components/GalleryView";
import PlantHeritage from "@/components/PlantHeritage";
import LivestockUnits from "@/components/LivestockUnits";
import { practiceLabels } from "@/components/PracticeSelector";
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
  MapPin, Sprout, Ruler, TreeDeciduous, Clock, Grid3x3, GitBranch, Globe, Mail,
  ExternalLink, Droplets, Mountain, Bird, Leaf, ArrowRight, Info, Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { fetchCultivarsByIds } from "@/lib/botanical";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { ProfileCompleteness } from "@/components/ProfileCompleteness";
import { formatLocation } from "@/components/FarmCard";
import { SciName } from "@/components/SciName";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { descriptionVersions, localizedProfileText } from "@/lib/profileLanguages";
import SubjectFields from "@/components/SubjectFields";
import LocationMap from '@/components/LocationMap';
import OpportunityPresentation from '@/components/OpportunityPresentation';
import { subjectLabel } from "@/lib/subjects";

const EXPERIENCE_TYPES = ["VISIT", "LEARN", "VOLUNTEER", "PROJECT"];
const SUPPORT_TYPES = ["ADOPT", "SUPPORT", "BUY"];

const Fact = ({ icon: Icon, children }) => (
  <span className="flex items-center gap-2 text-sm text-[#4A443D] font-sans">
    <Icon size={16} className="text-[#557448]" />
    {children}
  </span>
);

const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2A2521]">{children}</h2>
    {action}
  </div>
);

const PracticeBlock = ({ icon: Icon, title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-5">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-9 h-9 rounded-full bg-[#EAF0E6] flex items-center justify-center">
          <Icon size={17} className="text-[#557448]" />
        </span>
        <h3 className="font-serif text-lg font-semibold text-[#2A2521]">{title}</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className="px-3 py-1 rounded-full bg-[#F2ECE0] border border-[#E2DACB] text-sm text-[#4A443D] font-sans">
            {it}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function FarmProfile() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, tv, loc } = useLang();
  const { user } = useAuth();
  const [farm, setFarm] = useState(null);
  const [error, setError] = useState(false);
  const [speciesCatalog, setSpeciesCatalog] = useState([]);
  const [cultivarCatalog, setCultivarCatalog] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const controller = new AbortController();
    setFarm(null); setError(false); setCultivarCatalog([]);
    api.get(slug ? `/subjects/${encodeURIComponent(slug)}` : `/farms/${encodeURIComponent(id)}`, { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setFarm(data);
      fetchCultivarsByIds((data.botanical_selections || []).map((item) => item.cultivar_id), controller.signal)
        .then((records) => { if (!controller.signal.aborted) setCultivarCatalog(records); }).catch(() => {});
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    api.get("/botanical/species").then((r) => setSpeciesCatalog(r.data)).catch(() => {});
    return () => controller.abort();
  }, [id, slug]);

  useEffect(() => {
    if (farm?.public_path && farm.public_path !== location.pathname) {
      navigate(farm.public_path + location.search + location.hash, { replace: true });
    }
  }, [farm, location.pathname, location.search, location.hash, navigate]);

  usePageSeo({title:farm?.name, description:farm ? localizedProfileText(descriptionVersions(farm),lang,farm.original_language || 'it').text : '', image:farm?.cover_image || farm?.profile_image, noindex:!farm || !!error || !!farm.is_demo});
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-28 text-center">
        <h1 className="font-serif text-3xl text-[#2A2521]">{t("content.profileError")}</h1><p className="mt-3">{t("content.profileErrorText")}</p>
        <Link to="/explore" className="text-[#557448] mt-4 inline-block">← {t("explore.title")}</Link>
      </div>
    );
  }

  if (!farm) {
    return (
      <div className="h-[70vh] grain-bg flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-[#E2DACB] border-t-[#557448] animate-spin" />
      </div>
    );
  }

  const gardensOnly=(farm.activities || []).includes('gardens') && !(farm.activities || []).includes('agriculture');
  const agricultureActive = hasCropCultivation(farm.activities || ["agriculture"]);
  const descriptionText = localizedProfileText(descriptionVersions(farm), lang, farm.original_language || "it");
  const desc = descriptionText.text || t("content.noDescription");
  const qf = farm.quick_facts || {};
  const practices = farm.practices || {};
  const managedFields=(farm.fields || []).filter(field=>agricultureActive || field.reference_species_id);
  const hasPractices =
    managedFields.some(field => field.water_source || ["water_methods", "soil", "biodiversity", "agricultural"].some(key => field[key]?.length)) || practices.water_source || (practices.water_methods || []).length || (practices.soil || []).length ||
    (practices.biodiversity || []).length || (practices.agricultural || []).length;
  const products = (farm.brands || []).flatMap((b) => (b.products || []).map((p) => ({ ...p, brand: b })));
  const experiences = (farm.opportunities || []).filter((o) => o.active !== false && EXPERIENCE_TYPES.includes(o.type));
  const supports = (farm.opportunities || []).filter((o) => o.active !== false && SUPPORT_TYPES.includes(o.type));

  const tabs = [
    { v: "overview", label: t("farm.overview"), show: true },
    { v: "fields", label: gardensOnly ? (lang==="it"?"Orti e aree coltivate":"Food gardens and growing areas") : agricultureActive ? t("farm.fields") : (lang === "it" ? "Aree e piante" : "Areas and plants"), show: (farm.fields || []).some(field=>agricultureActive || field.reference_species_id) },
    { v: "crops", label: agricultureActive ? t("farm.cropsVarieties") : (lang==="it"?"Specie e varietà":"Species and cultivars"), show: hasPlantAreas(farm.activities || ["agriculture"]) && ((farm.reference_crop_ids || []).length > 0 || (farm.crops || []).length > 0 || (farm.botanical_selections || []).length > 0 || (farm.reference_botanical_selections || []).length > 0) },
    { v: 'fungi', label: lang==='it'?'Fungaia':'Mushroom cultivation', show: hasMushroomContent(farm) },
    { v: "grow", label: agricultureActive ? t("farm.howWeGrow") : (lang==="it"?"Gestione e pratiche":"Management and practices"), show: !!hasPractices },
    { v: "products", label: t("farm.products"), show: products.length > 0 },
    { v: "experiences", label: t("farm.experiences"), show: experiences.length > 0 },
    { v: "support", label: t("farm.supportAdopt"), show: supports.length > 0 },
  ].filter((x) => x.show);

  return (
    <div data-testid="farm-profile-page">
      {/* Header image */}
      <div className="relative h-72 sm:h-80 bg-[#557448]" style={!farm.cover_image ? { backgroundImage: 'radial-gradient(ellipse at top right, #84966c, transparent 70%), linear-gradient(135deg, #557448, #34492f)' } : undefined}>
        {farm.cover_image && <img src={farm.cover_image} alt="" className="w-full h-full object-cover" />}
        {farm.cover_image && <div className="absolute inset-0 bg-gradient-to-t from-[#2A2521]/80 via-[#2A2521]/15 to-transparent" />}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-7">
            <div className="flex items-center gap-3 sm:gap-4">
              {farm.profile_image ? <img src={farm.profile_image} alt={farm.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-4 border-[#F8F5EA] bg-[#F8F5EA] object-contain p-1 shrink-0" /> : <div aria-hidden="true" className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-4 border-[#F8F5EA] bg-[#F8F5EA] text-[#557448] text-4xl flex items-center justify-center shrink-0">{farm.name?.slice(0, 1)}</div>}
              <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span style={{ textShadow: "-.6px -.6px 0 #34492f, .6px -.6px 0 #34492f, -.6px .6px 0 #34492f, .6px .6px 0 #34492f" }} className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-white">
                {subjectLabel(farm.subject_type, lang)}
              </span>
              {farm.is_demo && (
                <span className="bg-[#2A2521]/70 text-[#F8F5EA] text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
                  {t("farm.demoFarm")}{farm.demo_version === "registration-v2" && farm.demo_plan ? ` · ${farm.demo_plan === "pro" ? "Pro" : "Free"}` : ""}
                </span>
              )}
            </div>
            <h1 data-testid="farm-name" style={{ textShadow: "0 2px 5px rgba(0,0,0,.65)" }} className="font-serif text-3xl sm:text-5xl font-bold text-[#F8F5EA] leading-tight break-words">
              {farm.name}
            </h1>
            <p className="flex items-center gap-1.5 text-[#F3EFE2] font-sans mt-2">
              <MapPin size={16} className="text-[#E3B24C]" />
              {formatLocation(farm.location, tv)}
            </p>
              </div>
            </div>
            {user && (user.is_admin || (user.farm_ids || []).includes(farm.id)) && (
              <Link
                to={`/dashboard/farms/${farm.id}/edit`}
                data-testid="profile-edit-farm"
                className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-[#F8F5EA] text-[#2A2521] font-sans text-sm font-semibold px-4 h-9 hover:bg-white transition-colors"
              >
                <Pencil size={14} /> {t("auth.editFarm")}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Quick facts */}
      <div className="bg-[#FFFDF8] border-b border-[#E2DACB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-x-7 gap-y-3" data-testid="quick-facts">
          {qf.crop_type && <Fact icon={Sprout}>{tv(qf.crop_type)}</Fact>}
          {qf.area_ha != null && <Fact icon={Ruler}>~{qf.area_ha} ha</Fact>}
          {qf.trees != null && <Fact icon={TreeDeciduous}>~{qf.trees} {t("farm.trees").toLowerCase()}</Fact>}
          {qf.orchard_age_years != null && <Fact icon={Clock}>~{qf.orchard_age_years} {t("farm.yearsOld")}</Fact>}
          {qf.planting_spacing && <Fact icon={Grid3x3}>{qf.planting_spacing}</Fact>}
        </div>
      </div>

      <Tabs defaultValue="overview" className="grain-bg min-h-[40vh]">
        <div className="sticky top-[72px] z-[500] bg-[#F8F5EA]/90 backdrop-blur border-b border-[#E2DACB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TabsList className="h-auto bg-transparent p-0 gap-1 overflow-x-auto flex justify-start w-full rounded-none">
              {tabs.map((tb) => (
                <TabsTrigger
                  key={tb.v}
                  value={tb.v}
                  data-testid={`tab-${tb.v}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#557448] data-[state=active]:text-[#557448] data-[state=active]:shadow-none text-[#6E6258] font-sans font-medium px-4 py-3.5 whitespace-nowrap bg-transparent"
                >
                  {tb.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6">
                  <SectionTitle>{t("farm.aboutFarm")}</SectionTitle>
                  <p className="font-sans text-[#4A443D] leading-relaxed">{desc}</p>
                  {descriptionText.fallback && <p className="text-sm text-[#6E6258] mt-2">{lang === "it" ? "Testo originale in inglese · traduzione non disponibile" : "Original text in Italian · translation unavailable"}</p>}
                  <div className="mt-5"><SocialLinks value={farm.contact?.social_links}/></div><SubjectVideo video={farm.video}/>
                  <LocationMap location={farm.location} name={farm.name} />
                  <div className="mt-6"><SubjectFields value={farm} readOnly />

                <PlantHeritage value={farm.plant_heritage || []} readOnly />
                {farm.activities?.includes("livestock") && <LivestockUnits value={farm.livestock_units || []} readOnly />}</div>
                  <div className="flex flex-wrap gap-5 mt-6 pt-5 border-t border-[#EDE7D7]">
                    {farm.location?.lat != null && farm.location?.lng != null ? (
                      <Link
                        to={`/explore?focus=${farm.id}`}
                        data-testid="farm-location-link"
                        className="flex items-center gap-2 text-sm text-[#557448] font-sans hover:underline"
                      >
                        <MapPin size={15} className="text-[#C46A3A]" />
                        {formatLocation(farm.location, tv)}
                      </Link>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-[#4A443D] font-sans">
                        <MapPin size={15} className="text-[#C46A3A]" />
                        {formatLocation(farm.location, tv)}
                      </div>
                    )}
                    {farm.contact?.website && (
                      <a href={farm.contact.website} target="_blank" rel="noreferrer" data-testid="overview-website"
                        className="flex items-center gap-2 text-sm text-[#557448] font-sans hover:underline">
                        <Globe size={15} /> {t("farm.website")}
                      </a>
                    )}
                    {farm.contact?.email && (
                      <a href={`mailto:${farm.contact.email}`} data-testid="overview-email"
                        className="flex items-center gap-2 text-sm text-[#557448] font-sans hover:underline">
                        <Mail size={15} /> {farm.contact.email}
                      </a>
                    )}
                  </div>
                  <div className="flex items-start gap-2 mt-5 text-xs text-[#9A8F80] font-sans bg-[#F3EEE0] rounded p-3">
                    <Info size={14} className="mt-0.5 shrink-0 text-[#C46A3A]" />
                    <span>{t("farm.declaredNote")}</span>
                  </div>
                </div>

                {agricultureActive && (farm.crops || []).length > 0 && (
                  <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6">
                    <SectionTitle>{t("farm.mainCrops")}</SectionTitle>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {farm.crops.map((c) => (
                        <div key={c.name} className="flex gap-3 items-center">
                          {c.image && <img src={c.image} alt={c.name} className="w-16 h-16 rounded-md object-cover border border-[#E2DACB]" />}
                          <div>
                            <h4 className="font-serif text-lg text-[#2A2521]">{tv(c.name)}</h4>
                            {c.scientific_name && <p className="text-sm text-[#6E6258] font-sans"><SciName name={c.scientific_name} /></p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <ProfileCompleteness completeness={farm.completeness} />
                {farm.gallery?.length > 0 && (
                  <GalleryView gallery={farm.gallery} />
                )}
              </div>
            </div>
          </TabsContent>

          {/* FIELDS */}
          {hasMushroomContent(farm) && <TabsContent value="fungi" className="mt-0"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"><h2 className="font-serif text-3xl mb-5">{lang==='it'?'Fungaia e funghi coltivati':'Mushroom farm and cultivated fungi'}</h2><MushroomCultivation value={farm} readOnly/><div className="mt-5"><SubjectFields value={farm} readOnly mode="fungi"/></div><p className="text-sm text-[#6E6258] mt-4">{lang==='it'?'Superfici riferite alle singole aree di coltivazione, senza somma automatica. Informazioni dichiarate dal soggetto.':'Figures refer to individual cultivation areas and are not automatically added together. Information declared by the profile holder.'}</p></div></TabsContent>}

          <TabsContent value="fields" className="mt-0">
            <SectionTitle>{gardensOnly ? (lang==="it"?"Orti e aree coltivate":"Food gardens and growing areas") : agricultureActive ? t("farm.ourFields") : (lang === "it" ? "Aree e patrimonio vegetale" : "Areas and plant heritage")}</SectionTitle>
            <div className="space-y-5">
              {(farm.fields || []).filter(field=>agricultureActive || field.reference_species_id).map((f) => (
                <div key={f.id} data-testid={`field-${f.id}`} className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] overflow-hidden grid grid-cols-1 sm:grid-cols-3">
                  {f.image && <img src={f.image} alt={loc(f.name)} className="h-48 sm:h-full w-full object-cover sm:col-span-1" />}
                  <div className={`p-6 ${f.image ? "sm:col-span-2" : "sm:col-span-3"}`}>
                    <h3 className="font-serif text-xl font-semibold text-[#2A2521]">{loc(f.name)}</h3>
                    {f.location_text && (
                      <p className="flex items-center gap-1.5 text-sm text-[#6E6258] font-sans mt-1">
                        <MapPin size={14} className="text-[#C46A3A]" /> {f.location_text}
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
                      {f.area_ha != null && <KV icon={Ruler} label={t("farm.area")} value={`~${f.area_ha} ha`} />}
                      {f.crop && <KV icon={Sprout} label={t("farm.crop")} value={<CropIdentity cropId={f.reference_crop_id} name={f.crop} />} />}
                      {f.trees != null && <KV icon={TreeDeciduous} label={t("farm.trees")} value={`~${f.trees}`} />}
                      {(metadataText(f,"mycorrhiza",lang) || f.mycorrhizal_fungi?.length>0) && <KV icon={Sprout} label={lang === "it" ? "Micorrizazione / fungo associato (dichiarato)" : "Mycorrhization / associated fungus (declared)"} value={<MycorrhizaSummary row={f} lang={lang} />} />}
                      {metadataText(f,"planting_shape",lang) && <KV icon={GitBranch} label={lang === "it" ? "Forma di allevamento" : "Training system"} value={metadataText(f,"planting_shape",lang)} />}
                      {f.orchard_age != null && <KV icon={Clock} label={t("farm.orchardAge")} value={`~${f.orchard_age} ${t("farm.years")}`} />}
                      {(metadataText(f,"planting_spacing",lang) || (f.row_spacing != null && f.plant_spacing != null)) && <KV icon={Grid3x3} label={t("farm.plantingSpacing")} value={metadataText(f,"planting_spacing",lang) || `${f.row_spacing} × ${f.plant_spacing} ${f.spacing_unit || "m"}`} />}

                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CROPS */}
          <TabsContent value="crops" className="mt-0">
            <SectionTitle>{t("farm.cropsVarieties")}</SectionTitle>
            <ReferenceCatalog activities={farm.activities || ["agriculture"]} value={farm} readOnly />
            {(farm.botanical_selections || []).length > 0 && (
              <div className="mb-6 rounded-md border border-[#E2DACB] bg-[#FFFDF8] p-6" data-testid="botanical-profile">
                <h3 className="font-serif text-xl font-semibold text-[#2A2521] mb-4">{t("farm.botanicalDetails")}</h3>
                <div className="space-y-4">
                  {Array.from(new Set(farm.botanical_selections.map((item) => item.species_id))).map((speciesId) => {
                    const species = speciesCatalog.find((item) => item.id === speciesId);
                    const selections = farm.botanical_selections.filter((item) => item.species_id === speciesId);
                    const cultivarNames = Array.from(new Set(selections.map((item) =>
                      cultivarCatalog.find((cultivar) => cultivar.id === item.cultivar_id)?.name
                    ).filter(Boolean)));
                    const customNames = Array.from(new Set(selections.map((item) =>
                      item.custom_cultivar
                    ).filter(Boolean)));
                    return (
                      <div key={speciesId} className="border-b border-[#EDE7D7] last:border-0 pb-3 last:pb-0">
                        <p className="font-sans font-medium text-[#2A2521]">
                          {species?.common_names?.[lang] || species?.common_names?.en || speciesId}
                          {species && <span className="text-sm font-normal text-[#6E6258] ml-2"><SciName name={species.scientific_name} /></span>}
                        </p>
                        {(cultivarNames.length > 0 || customNames.length > 0) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {cultivarNames.map((name) => (
                              <span key={name} className="rounded-full bg-[#EAF0E6] border border-[#BED2B5] px-3 py-1 text-sm text-[#3E5733]">{name}</span>
                            ))}
                            {customNames.map((name) => (
                              <span key={name} title={lang === "it" ? "Dichiarata dal soggetto, non verificata" : "Declared by the profile holder, unverified"} className="rounded-full bg-[#FAF1E4] border border-[#EDD8B6] px-3 py-1 text-sm text-[#A6761F]">{name} *</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-5">
              {(farm.crops || []).map((c) => (
                <div key={c.name} data-testid={`crop-row-${c.name}`} className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6 flex flex-col sm:flex-row gap-5">
                  {c.image && <img src={c.image} alt={c.name} className="w-full sm:w-40 h-40 rounded-md object-cover border border-[#E2DACB]" />}
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl font-semibold text-[#2A2521]">{tv(c.name)}</h3>
                    {c.scientific_name && (
                      <p className="text-[#6E6258] font-sans mt-0.5"><SciName name={c.scientific_name} /></p>
                    )}
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#C46A3A] font-sans mb-2">{t("farm.varieties")}</p>
                      <div className="flex flex-wrap gap-2">
                        {(c.varieties || []).map((v) => {
                          const tbv = v.trim().toLowerCase() === "to be verified";
                          return (
                            <span key={v} className={`px-3 py-1 rounded-full text-sm font-sans border ${tbv ? "bg-[#FAF1E4] border-[#EDD8B6] text-[#A6761F] italic" : "bg-[#EAF0E6] border-[#BED2B5] text-[#3E5733]"}`}>
                              {tbv ? t("farm.toBeVerified") : v}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <Link to={`/crops/${c.name.toLowerCase()}`} className="inline-flex items-center gap-1.5 mt-5 text-[#557448] font-sans text-sm font-medium hover:gap-2.5 transition-all">
                      {t("farm.learnMore")} <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* HOW WE GROW */}
          <TabsContent value="grow" className="mt-0">
            <SectionTitle>{t("farm.howWeGrow")}</SectionTitle>
            {!!managedFields.length ? <div className="space-y-6">{managedFields.map(field => <section key={field.id} className="border rounded-md bg-[#FFFDF8] p-5">
              <h3 className="font-serif text-xl mb-4"><CropIdentity cropId={field.reference_crop_id} name={field.crop} /> · {loc(field.name)}</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <PracticeBlock icon={Droplets} title={t('farm.waterManagement')} items={[...practiceLabels('water_methods',field.water_methods,lang,field.text_translations), ...(field.water_source ? [`${t('farm.waterSource')}: ${practiceLabels('water_source',field.water_source,lang,field.text_translations).join(', ')}`] : [])]} />
                <PracticeBlock icon={Mountain} title={t('farm.soilManagement')} items={practiceLabels('soil',field.soil,lang,field.text_translations)} />
                <PracticeBlock icon={Bird} title={t('farm.biodiversity')} items={practiceLabels('biodiversity',field.biodiversity,lang,field.text_translations)} />
                <PracticeBlock icon={Leaf} title={t('farm.agriculturalPractices')} items={practiceLabels('agricultural',field.agricultural,lang,field.text_translations)} />
              </div>
            </section>)}</div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-9 h-9 rounded-full bg-[#E4EEF2] flex items-center justify-center">
                    <Droplets size={17} className="text-[#4A7C95]" />
                  </span>
                  <h3 className="font-serif text-lg font-semibold text-[#2A2521]">{t("farm.waterManagement")}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {practiceLabels("water_methods",practices.water_methods,lang,practices.text_translations).map((m) => (
                    <span key={m} className="px-3 py-1 rounded-full bg-[#F2ECE0] border border-[#E2DACB] text-sm text-[#4A443D] font-sans">{m}</span>
                  ))}
                  {practices.water_source && (
                    <span className="px-3 py-1 rounded-full bg-[#EAF0E6] border border-[#BED2B5] text-sm text-[#3E5733] font-sans">
                      {t("farm.waterSource")}: {practiceLabels("water_source", practices.water_source, lang,practices.text_translations).join(", ")}
                    </span>
                  )}
                </div>
                {practices.water_note && (
                  <p className="flex items-start gap-2 text-xs text-[#9A8F80] font-sans mt-3">
                    <Info size={13} className="mt-0.5 shrink-0 text-[#C46A3A]" /> {loc(practices.water_note)}
                  </p>
                )}
              </div>
              <PracticeBlock icon={Mountain} title={t("farm.soilManagement")} items={practiceLabels("soil",practices.soil,lang,practices.text_translations)} />
              <PracticeBlock icon={Bird} title={t("farm.biodiversity")} items={practiceLabels("biodiversity",practices.biodiversity,lang,practices.text_translations)} />
              <PracticeBlock icon={Leaf} title={t("farm.agriculturalPractices")} items={practiceLabels("agricultural",practices.agricultural,lang,practices.text_translations)} />
            </div>}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-0">
            <SectionTitle>{t("farm.fromThisFarm")}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <div key={p.id} data-testid={`product-${p.id}`} className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] overflow-hidden flex flex-col">
                  {p.image && <img src={p.image} alt={loc(p.name)} className="h-72 w-full object-contain bg-white p-3" />}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#C46A3A] font-sans">{p.brand?.name}</p>
                    <h3 className="font-serif text-xl font-semibold text-[#2A2521] mt-1">{loc(p.name)}</h3>
                    {p.description && <p className="text-sm text-[#6E6258] font-sans mt-1.5 flex-1">{loc(p.description)}</p>}
                    {(p.external_url || p.brand?.shop_url) && (
                      <a href={p.external_url || p.brand.shop_url} target="_blank" rel="noopener noreferrer"
                        data-testid={`visit-shop-${p.id}`}
                        className="inline-flex items-center justify-center gap-1.5 mt-4 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] text-sm font-medium font-sans h-10 transition-colors">
                        {p.cta_label ? loc(p.cta_label) : (<>{t("farm.visitShop")} <ExternalLink size={14} /></>)}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* EXPERIENCES */}
          <TabsContent value="experiences" className="mt-0">
            <SectionTitle>{t("farm.experiences")}</SectionTitle>
            <OpportunityList farm={farm} items={experiences} />
          </TabsContent>

          {/* SUPPORT & ADOPT */}
          <TabsContent value="support" className="mt-0">
            <SectionTitle>{t("farm.supportAdopt")}</SectionTitle>
            <OpportunityList farm={farm} items={supports} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

const KV = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-[#9A8F80] font-sans flex items-center gap-2">{Icon && <Icon size={16} className="text-[#557448] shrink-0" />}{label}</p>
    <p className="font-sans text-[#2A2521] font-medium mt-0.5">{value}</p>
  </div>
);

const OpportunityList = ({items, farm}) => <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{items.map(opportunity => <OpportunityPresentation key={opportunity.id} opportunity={opportunity} farm={farm} />)}</div>;

